import {Injectable} from '@angular/core';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, delay, map, switchMap} from 'rxjs/operators';
import {LoggerService} from '../logger.service';
import {CaptchaService} from "./captcha.service";

declare const AwsWafIntegration: any;

@Injectable({
  providedIn: 'root'
})
export class AwsWafWrapperService {

  private static readonly retryInterval = 100;
  private static readonly maxRetries = 50;

  constructor(private captchaService: CaptchaService,
              private loggerService: LoggerService) {
  }

  public get(url: string): Observable<any> {
    return this.fetchWithRetry(url, {method: 'GET', headers: {}});
  }

  public post(url: string, body: any, headers: any): Observable<any> {
    return this.fetchWithRetry(url, {method: 'POST', headers: headers, body: body});
  }

  private fetchWithRetry(
    url: string,
    options: any,
    getWafTokenFunction: Observable<any> = this.getAwsWafTokenSafely(false),
    challengeRetriesLeft: number = AwsWafWrapperService.maxRetries
  ): Observable<any> {
    return getWafTokenFunction.pipe(
      switchMap((token: string) => {
        options.headers['x-aws-waf-token'] = token;
        return from(AwsWafIntegration.fetch(url, options));
      }),
      switchMap((response: Response) => this.handleFetchResponse(response, url, options, challengeRetriesLeft)),
      catchError((error: any) => this.handleFetchError(error, url))
    );
  }

  private handleFetchResponse(
    response: Response,
    url: string,
    options: any,
    challengeRetriesLeft: number
  ): Observable<any> {
    switch (response.status) {
      case 200:
        this.loggerService.info(`${url} API call completed successfully`);
        return from(response.json());
      case 202:
        return this.handleChallengeResponse(url, options, challengeRetriesLeft);
      case 405:
        return this.handleCaptchaResponse(url, options);
      default:
        this.loggerService.ddError(`${url} API call failed with status code ${response.status}`);
        return throwError(() => new Error(`Http response is unsuccessful: ${response.status}`));
    }
  }

  private handleChallengeResponse(
    url: string,
    options: any,
    challengeRetriesLeft: number
  ): Observable<any> {
    if (challengeRetriesLeft > 0) {
      this.loggerService.ddInfo(`${url} API call failed with Challenge, retrying... Retries left: ${challengeRetriesLeft - 1}`);
      return this.fetchWithRetry(url, options, this.getAwsWafTokenSafely(true), challengeRetriesLeft - 1);
    }
    return throwError(() => new Error('Max retries reached without resolving the challenge.'));
  }

  private handleCaptchaResponse(
    url: string,
    options: any
  ): Observable<any> {
    this.loggerService.ddInfo(`${url} API call failed with Captcha`);
    return this.captchaService.handleCaptchaResponse()
      .pipe(switchMap(token => this.fetchWithRetry(url, options, of(token))));
  }

  private handleFetchError(error: any, url: string): Observable<never> {
    this.loggerService.ddError(`Error during ${url} API call: ${error}`, error);
    return throwError(() => new Error(`API call error: ${error.message || error}`));
  }

  private getAwsWafTokenSafely(forceRefreshToken: boolean): Observable<any> {
    return this.waitForAwsWafIntegration().pipe(
      switchMap(() => this.getAwsWafToken(forceRefreshToken)),
      catchError(error => throwError(() => new Error('Error loading AWS WAF script or getting token: ' + error)))
    );
  }

  private waitForAwsWafIntegration(): Observable<void> {
    return this.retryWithDelay(() => this.queryChallengeScriptElement(), AwsWafWrapperService.maxRetries)
      .pipe(switchMap(script =>
        new Observable<void>(observer => {
          const originalOnload = script.onload;
          script.onload = (event: Event) => {
            if (originalOnload) {
              originalOnload.call(script, event);
            }
            observer.next();
            observer.complete();
          };
          if (typeof (window as any).AwsWafIntegration !== 'undefined') {
            observer.next();
            observer.complete();
            return;
          }
        })
      ));
  }

  private queryChallengeScriptElement() {
    return new Observable<HTMLScriptElement>(observer => {
      let challengeScriptElement = Array.from(document.querySelectorAll('script'))
        .find(script => script.src.includes('/challenge')) as HTMLScriptElement;
      if (!challengeScriptElement) {
        observer.error(new Error('AWS WAF Challenge script is not found'));
      } else {
        observer.next(challengeScriptElement);
      }
      observer.complete();
    });
  }

  private getAwsWafToken(forceRefreshToken = false): Observable<any> {
    let operation = () => {
      if (forceRefreshToken) {
        return AwsWafIntegration.forceRefreshToken();
      }
      return AwsWafIntegration.getToken();
    };
    return from(operation()).pipe(
      map(token => {
        if (token) {
          return token;
        } else {
          throw new Error('Empty token');
        }
      }),
      catchError(error => {
        console.error('Error getting token:', error);
        throw error;
      })
    );
  }

  private retryWithDelay<T>(operation: () => Observable<T>, retriesLeft: number, retryInterval = AwsWafWrapperService.retryInterval): Observable<T> {
    return operation().pipe(
      catchError(error => {
        if (retriesLeft > 0) {
          console.log(`Operation failed, retrying... Retries left: ${retriesLeft - 1}`);
          return of(null).pipe(
            delay(retryInterval),
            switchMap(() => this.retryWithDelay(operation, retriesLeft - 1))
          );
        } else {
          return throwError(() => error);
        }
      })
    );
  }
}
